/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState } from 'react';
import {
  CheckIcon,
  InfoIcon,
  FlagIcon,
  AlertCircleIcon,
  HelpCircleIcon,
} from 'lucide-react';
import WButton from 'shared_components/components/WForms/WButton/WButton';
interface CategoryData {
  id: string;
  label: string;
  value: number;
  text: string;
  color: string;
  icon: React.ReactNode;
  description: string;
}
interface VerificationChartProps {
  data: CategoryData[];
  onFilterChange?: (categoryId: string | null) => void;
}
// Calculate adjusted widths ensuring minimum 10% per section
function calculateAdjustedWidths(data: CategoryData[]): Map<string, number> {
  const MIN_WIDTH = 5;
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const widths = new Map<string, number>();
  // Calculate natural percentages
  const naturalPercentages = data.map((item) => ({
    id: item.id,
    percentage: (item.value / total) * 100,
    value: item.value,
  }));
  // Separate items into those below and above minimum
  const belowMin = naturalPercentages.filter(
    (item) => item.percentage < MIN_WIDTH
  );
  const aboveMin = naturalPercentages.filter(
    (item) => item.percentage >= MIN_WIDTH
  );
  // Calculate space used by minimum widths
  const minWidthSpace = belowMin.length * MIN_WIDTH;
  // Calculate remaining space for proportional distribution
  const remainingSpace = 100 - minWidthSpace;
  // Calculate total value of items above minimum
  const aboveMinTotal = aboveMin.reduce((sum, item) => sum + item.value, 0);
  // Set minimum widths for items below threshold
  belowMin.forEach((item) => {
    widths.set(item.id, MIN_WIDTH);
  });
  // Distribute remaining space proportionally among items above minimum
  aboveMin.forEach((item) => {
    const proportionalWidth = (item.value / aboveMinTotal) * remainingSpace;
    widths.set(item.id, proportionalWidth);
  });
  return widths;
}
export function VerificationChart({
  data,
  onFilterChange,
}: VerificationChartProps) {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  // Filter out items with 0 value
  const filteredData = data.filter((item) => item.value > 0);
  const total = filteredData.reduce((sum, item) => sum + item.value, 0);
  const activeCategory = filteredData.find((c) => c.id === activeFilter);
  const adjustedWidths = calculateAdjustedWidths(filteredData);
  const handleBarClick = (categoryId: string) => {
    const newFilter = activeFilter === categoryId ? null : categoryId;
    setActiveFilter(newFilter);
    onFilterChange?.(newFilter);
  };
  const handleReset = () => {
    setActiveFilter(null);
    onFilterChange?.(null);
  };
  return (
    <div className="tw-w-full tw-rounded-lg tw-flex tw-gap-4 tw-items-end">
      <div className="tw-h-full tw-flex tw-items-end tw-pb-5">
        <span className="tw-text-lg tw-font-semibold tw-text-white tw-h-8">
          Role Data Quality
        </span>
      </div>
      <div className="tw-flex-grow">
        <div className="tw-flex tw-justify-end tw-items-center tw-mb-2">
          {!activeFilter ? (
            <div className="tw-text-sm tw-text-gray-400">
              Click section to filter
            </div>
          ) : activeCategory ? (
            <div className="tw-flex tw-items-center tw-gap-3">
              <div className="tw-text-sm tw-text-gray-400">
                Filtering:{' '}
                <span className="tw-text-white tw-font-medium">
                  {activeCategory.label}
                </span>
              </div>
              <WButton
                onClick={handleReset}
                variant="secondary"
                label={'Reset Filter'}
              />
            </div>
          ) : null}
        </div>
        <div className="tw-relative">
          <div className="tw-relative tw-flex tw-gap-1">
            {filteredData.map((category, index) => {
              const adjustedWidth = adjustedWidths.get(category.id) || 0;
              const isActive = activeFilter === category.id;
              const naturalPercentage = (
                (category.value / total) *
                100
              ).toFixed(1);
              return (
                <div
                  key={category.id}
                  className={`tw-relative tw-gap-1 tw-flex tw-flex-col tw-items-end tw-justify-end tw-cursor-pointer tw-transition-all tw-duration-200 ${
                    isActive
                      ? 'tw-opacity-100'
                      : activeFilter
                      ? 'tw-opacity-40'
                      : 'tw-opacity-100'
                  }`}
                  style={{
                    width: `calc(${adjustedWidth}%)`,
                    borderRight: '2px solid #858d93',
                  }}
                  onClick={() => handleBarClick(category.id)}
                  onMouseEnter={() => setHoveredCategory(category.id)}
                  onMouseLeave={() => setHoveredCategory(null)}
                >
                  <div className="tw-w-full tw-flex tw-justify-end tw-pr-1">
                    <div
                      className="tw-w-5 tw-h-5"
                      style={{
                        color: category.color,
                      }}
                    >
                      {category.icon}
                    </div>
                  </div>
                  {/* Always show numeric value */}
                  <span
                    className="tw-flex tw-justify-end tw-items-center tw-font-semibold tw-text-sm tw-w-full tw-text-right tw-pr-1 tw-h-8"
                    style={{
                      backgroundColor: category.color,
                      color: category.text,
                    }}
                  >
                    {category.value.toLocaleString('en-US')}
                  </span>
                  <span className="tw-text-gray-400 tw-text-xs tw-font-medium tw-pr-1">
                    {naturalPercentage}%
                  </span>
                </div>
              );
            })}
          </div>
          {/* Tooltips for descriptions on hover */}
          {filteredData.map((category, index) => {
            const isHovered = hoveredCategory === category.id;
            if (!isHovered) return null;
            const adjustedWidth = adjustedWidths.get(category.id) || 0;
            // Calculate cumulative position
            let cumulativePosition = 0;
            for (let i = 0; i < index; i++) {
              cumulativePosition += adjustedWidths.get(filteredData[i].id) || 0;
            }
            const sectionStart = cumulativePosition;
            const sectionEnd = cumulativePosition + adjustedWidth;
            const sectionCenter = (sectionStart + sectionEnd) / 2;
            // Determine tooltip positioning
            let tooltipPositionStyle: React.CSSProperties = {
              left: `${sectionCenter}%`,
              transform: 'translateX(-50%)',
            };
            let arrowPositionClasses =
              'tw-left-1/2 tw-transform tw--translate-x-1/2';
            if (sectionCenter < 33) {
              tooltipPositionStyle = {
                left: `${sectionStart}%`,
              };
              arrowPositionClasses = 'tw-left-4';
            } else if (sectionCenter > 66) {
              tooltipPositionStyle = {
                right: `${100 - sectionEnd}%`,
              };
              arrowPositionClasses = 'tw-right-4';
            }
            return (
              <div
                key={`tooltip-${category.id}`}
                className="tw-absolute tw-h-[36px] tw-bottom-full tw-mb-2 tw-px-3 tw-py-2 tw-bg-[#2a2a2a] tw-text-white tw-text-xs tw-rounded-lg tw-shadow-xl tw-whitespace-nowrap tw-z-50 tw-border tw-border-gray-700 tw-pointer-events-none"
                style={{
                  ...tooltipPositionStyle,
                  top: '-18px',
                }}
              >
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-justify-center">
                  <div
                    className="tw-w-5 tw-h-5"
                    style={{
                      color: category.color,
                    }}
                  >
                    {category.icon}
                  </div>
                  <span className="tw-font-semibold">{category.label}</span>
                </div>
                {/* <div className="tw-text-gray-300 tw-text-[11px]">
                  {category.description}
                </div> */}
                <div
                  className={`tw-absolute tw-top-full ${arrowPositionClasses} tw--mt-px`}
                >
                  <div className="tw-border-[5px] tw-border-transparent tw-border-t-[#2a2a2a]"></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
export function createDefaultChartData(): CategoryData[] {
  return [
    {
      id: 'verified',
      label: 'Verified (No Effort)',
      value: 45,
      text: 'white',
      color: '#22c55e',
      icon: <CheckIcon className="tw-w-3.5 tw-h-3.5" />,
      description:
        'Contributions that have been verified and require no additional effort',
    },
    {
      id: 'inferred',
      label: 'Inferred (Low Effort)',
      value: 30,
      text: 'white',
      color: '#eab308',
      icon: <InfoIcon className="tw-w-3.5 tw-h-3.5" />,
      description:
        'Contributions that can be inferred with low verification effort',
    },
    {
      id: 'credited',
      label: 'Credited (Mid Effort)',
      value: 15,
      text: 'white',
      color: '#f97316',
      icon: <FlagIcon className="tw-w-3.5 tw-h-3.5" />,
      description:
        'Contributions that are credited and require moderate verification effort',
    },
    {
      id: 'unverified',
      label: 'Unverified (High Effort)',
      value: 5,
      text: 'white',
      color: '#ef4444',
      icon: <AlertCircleIcon className="tw-w-3.5 tw-h-3.5" />,
      description:
        'Contributions that are unverified and require high verification effort',
    },
    {
      id: 'unknown',
      label: 'Unknown (Max Effort)',
      value: 2,
      text: 'white',
      color: '#6b7280',
      icon: <HelpCircleIcon className="tw-w-3.5 tw-h-3.5" />,
      description:
        'Contributions with unknown status requiring maximum verification effort',
    },
  ];
}
